/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {Drawer} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {PlatformDeviceTypes} from '@ion/core/src/types/entities';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import RadioButton from '@ion/dls/src/components/radio/RadioButton';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {navCollapsedStatusSelector} from '@/store/selectors/navigation';
import JobFacetsLoader from './JobFacetsLoader';
import {
  Aggregation,
  allDateFacetsEmpty,
  containsAggregationTerm,
  dateFacets,
  DateFacetType,
  getFilteredAggregations,
  updateFacetCounts
} from './JobFacetUtils';
import {DRAWER_WIDTH} from './JobSearchEngine';
import PlatformFacetAccordion from './PlatformFacetAccordion';
import {useJobPagination} from './useJobPagination';
import useJobSelectedFacets from './useJobSelectedFacets';

export const MAX_INITIAL_FACETS        = 5;
export const MAX_INITIAL_FACETS_GROUPS = 3;

interface JobFacetsDrawerProps {
  aggregations: Aggregation[];
  isFacetOpen: boolean;
  deviceTypes?: PlatformDeviceTypes;
  setDateRange?: (dateRange: DateFacetType | null) => void;
  activeTab: string;
  facetCategories: string[];
  hideDateFacets?: boolean;
  isLoading?: boolean;
  setIsFileTypeChecked?: (isFileTypeChecked: boolean) => void;
  setPlatformId?: (platformId: string) => void;
  otaCode?: Object;
  onRefresh: Function;
}

const useStyles = makeStyles(() => ({
  drawer : {
    width           : DRAWER_WIDTH,
    flexShrink      : 0,
    backgroundColor : `${dlsColor('grey5')} !important`
  },
  drawerPaper : {
    height          : 'calc(100vh - 205px)',
    backgroundColor : `${dlsColor('grey5')} !important`,
    boxSizing       : 'border-box',
    width           : DRAWER_WIDTH,
    marginLeft      : ({isFacetOpen, isNavCollapsed}: {isFacetOpen: boolean, isNavCollapsed: boolean}) => (
      (isFacetOpen && !isNavCollapsed) ? '105px' : '50px'
    ),
    overflowY  : 'scroll',
    transition : 'all 200ms cubic-bezier(0.4, 0.0, 0.2, 1) !important'
  },
  accordionWrapper : {
    borderBottom : `1px solid ${dlsColor('grey4')}`
  },
  accordion : {
    backgroundColor : `${dlsColor('grey5')} !important`,
    ...dlsAlt('typography', 'cta2')
  },
  categoryTitle : {
    minWidth       : '100%',
    color          : dlsColor('grey2'),
    width          : '185px !important',
    overflow       : 'hidden',
    textOverflow   : 'ellipsis',
    whiteSpace     : 'nowrap',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between'
  },
  clearAction : {
    ...dlsAlt('typography', 'cta2'),
    color     : dlsColor('aaBlue'),
    padding   : '4px',
    cursor    : 'pointer',
    '&:hover' : {
      textDecoration : 'underline'
    }
  },
  showAction : {
    margin    : '8px 0px 0px -4px',
    ...dlsAlt('typography', 'cta2'),
    color     : dlsColor('aaBlue'),
    padding   : '4px',
    cursor    : 'pointer',
    '&:hover' : {
      textDecoration : 'underline'
    }
  },
  details : {
    backgroundColor : `${dlsColor('grey5')} !important`,
    padding         : '0px 18px',
    display         : 'flex',
    flexDirection   : 'column'
  },
  checkBoxLabel : {
    fontSize : 14,
    color    : dlsColor('grey2')
  },
  checkBoxLabelDisabled : {
    fontSize : 14,
    color    : dlsColor('grey3'),
    opacity  : 0.9
  },
  checkBoxCount : {
    fontSize : 14,
    color    : dlsColor('grey3')
  },
  checkBoxCountDisabled : {
    fontSize : 14,
    color    : dlsColor('grey4')
  },
  radioButton : {
    maxHeight : '32px',
    padding   : '0px',
    margin    : '4px 5px 4px 6px'
  },
  checkBoxPosition : {
    padding : '0px 25px 10px !important'
  },
  noData : {
    height         : '100%',
    ...dlsAlt('typography', 'body1'),
    color          : dlsColor('grey3'),
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    fontSize       : '16px',
    padding        : '0px 0px 48px'
  },
  titleTooltipWrapper : {
    display      : 'flex',
    alignItems   : 'center',
    gap          : 9,
    justifyItems : 'center'
  }
}));

/**
 * The JobFacetsDrawer component is a filter drawer that allows users to refine 
 * job searches based on various facets, including categories and dates. It uses 
 * checkboxes and radio buttons to select facets and applies changes dynamically 
 * when a user makes a selection
 * 
 * ```typescript
 *  import JobFacetsDrawer from './JobFacetsDrawer';
 * 
 *  <JobFacetsDrawer aggregations={aggregations}
 *   isFacetOpen={isFacetOpen}
 *   facetCategories={['progressStatus', 'jobType', 'createdOn']} />
 * ```
 * 
 * {@link JobFacetsDrawerProps}
 * @param aggregations    - The list of facets to display in the drawer.
 * @param isFacetOpen     - The open / closed state of the drawer.
 * @param hideDateFacets  - A boolean value to hide date facets.
 * @param facetCategories - An array of facet categories to display in the 
 *                          drawer (e.g. ['progressStatus', etc.]).
 * @returns  The JobFacetsDrawer component
 */
const JobFacetsDrawer = ({
  aggregations,
  isFacetOpen,
  deviceTypes,
  setIsFileTypeChecked,
  setPlatformId,
  activeTab,
  setDateRange,
  facetCategories,
  hideDateFacets,
  isLoading,
  otaCode,
  onRefresh
}: JobFacetsDrawerProps) => {
  const isNavCollapsed                      = useSelector(navCollapsedStatusSelector);
  const classes                             = useStyles({isFacetOpen, isNavCollapsed});
  const [showMoreFacets, setShowMoreFacets] = useState<Record<string, boolean>>({});

  const {
    platformIdAggregations,
    filteredAggregations,
    dateAggregations
  } = getFilteredAggregations(aggregations, facetCategories);

  const dateAggs                        = updateFacetCounts(dateFacets, dateAggregations);
  const isPlatformAggregationsAvailable = !isEmpty(platformIdAggregations);
  const isAggregationsAvailable         = !isEmpty(filteredAggregations);
  const isDateAggregationsAvailable     = !allDateFacetsEmpty(dateAggs);

  const {
    getSelectedFacetsForTab,
    updateFacetsForTab,
    hasSelectedJobsFacets,
    clearJobFacetCategory
  } = useJobSelectedFacets();

  const {clearPaginationForTab} = useJobPagination();

  const handleFacetChange = useCallback(
    (facet: [string, string], isChecked: boolean, activeTab: string) => {
      const [facetCategory, facetValue] = facet;
      if (facetCategory === 'tasks.metadata.platformId-terms') {
        setPlatformId(facetValue);
        setIsFileTypeChecked(isChecked);
      }
      updateFacetsForTab(activeTab, [facetCategory, facetValue], isChecked);
      clearPaginationForTab(activeTab);
      onRefresh();
    }, [setPlatformId, setIsFileTypeChecked, activeTab, clearPaginationForTab]);

  const handleDateFacetChange = useCallback(
    (facetKey: string, isChecked: boolean, activeTab: string) => {
      const selectedDateFacet = dateFacets[facetKey];
      updateFacetsForTab(activeTab, ['createdOn', facetKey], isChecked);
      clearPaginationForTab(activeTab);
      setDateRange?.(selectedDateFacet.dates);
    },
    [setDateRange, activeTab, clearPaginationForTab]
  );

  const clearFacetCategory = useCallback((facetCategory: string, activeTab: string) => {
    clearJobFacetCategory(activeTab, facetCategory);
    clearPaginationForTab(activeTab);
    onRefresh();
  }, [clearPaginationForTab]);

  const toggleShowMore = useCallback((category: string) => {
    setShowMoreFacets((prevState) => ({
      ...prevState,
      [category] : !prevState[category]
    }));
  }, []);

  const hasOtherAggregations = (name) => {
    return !containsAggregationTerm(name, 'Status') && !containsAggregationTerm(name, 'ota');
  };

  return (
    <Drawer variant="persistent"
      anchor="left"
      open={isFacetOpen}
      classes={{paper: classes.drawerPaper}}>

      <ConditionalRender condition={isLoading && !isAggregationsAvailable}>
        <JobFacetsLoader />
      </ConditionalRender>

      <ConditionalRender condition={!isLoading && !isAggregationsAvailable}>
        <div className={classes.noData}>
          {translate('jobsearchengine:facets.noDataAvailable')}
        </div>
      </ConditionalRender>
      
      <ConditionalRender condition={isAggregationsAvailable}>
        {filteredAggregations?.map((aggregation) => {
          if (aggregation?.value?.buckets.length === 0) {
            return null;
          }
          const sortedBuckets = [...aggregation?.value?.buckets].sort((a, b) => a.key.localeCompare(b.key));
          const hasSelectedFacets = hasSelectedJobsFacets(activeTab, aggregation.name);
          const showAllFacets = showMoreFacets?.[aggregation?.name];
          const facetsToShow = showAllFacets
            ? sortedBuckets
            : sortedBuckets.slice(0, MAX_INITIAL_FACETS);
          //modifying the keys for otaStatCode & otaDiagCode based on the ota API
          if (aggregation.name.indexOf('ota') >= 0) {
            const parsedVal = safeJSONParse(otaCode);
            facetsToShow.map((facetKey) => {
              if (Object.keys(parsedVal).includes(facetKey.key)) {
                facetKey.label = parsedVal[facetKey.key];
              }
            });
          }

          return (
            <div key={aggregation.name} className={classes.accordionWrapper}>
              <Accordion className={classes.accordion}
                header={
                  <div className={classes.categoryTitle}>
                    {translate(`jobsearchengine:facets.category.${aggregation.name}.title`)}
                    {hasSelectedFacets && (
                      <div onClick={function(e) {
                        e.stopPropagation();
                        e.preventDefault();
                        clearFacetCategory(aggregation.name, activeTab);
                      }}
                      className={classes.clearAction}>
                        {translate('jobsearchengine:facets.clear')}
                      </div>
                    )}
                  </div>
                }
                details={
                  <div className={classes.details}>
                    {facetsToShow?.map((facet: any) => (
                      <CheckBox key={facet.key}
                        disabled={facet.count === 0 || isLoading}
                        label={
                          <div>
                            <span className={isLoading ? classes.checkBoxLabelDisabled : classes.checkBoxLabel}>
                              <ConditionalRender condition={containsAggregationTerm(aggregation?.name, 'Status')}>
                                {translate(`jobsearchengine:facets.category.progressStatus-terms.${facet.key?.toLowerCase()}`)}
                              </ConditionalRender>
                              <Render.If condition={containsAggregationTerm(aggregation?.name, 'ota')}>
                                {facet.label}
                              </Render.If>
                              <ConditionalRender condition={hasOtherAggregations(aggregation?.name)}>
                                {facet.key}
                              </ConditionalRender>
                            </span>
                            <span className={isLoading ? classes.checkBoxCountDisabled : classes.checkBoxCount}>
                              {` (${facet.count})`}
                            </span>
                          </div>
                        }
                        checked={getSelectedFacetsForTab(activeTab)[aggregation.name]?.includes(facet.key) || false}
                        onChange={function(e) {
                          return handleFacetChange([aggregation.name, facet.key], e.target.checked, activeTab);
                        }} />
                    ))}
                    {!getSelectedFacetsForTab(activeTab)?.[aggregation.name]?.length &&
                    aggregation?.value?.buckets.length > MAX_INITIAL_FACETS && (
                      <div className={classes.showAction}
                        onClick={function(e) {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleShowMore(aggregation.name);
                        }}>
                        {translate(`jobsearchengine:facets.${showAllFacets ? 'showLess' : 'showMore'}`)}
                      </div>
                    )}
                  </div>
                }
                backgroundColor={dlsColor('grey5')}
                borderBottom={'none'}
                padding={'0px 0px 15px'}
                isCondensedVariant
                defaultExpanded />
            </div>
          );
        })}
        <Render.If condition={isPlatformAggregationsAvailable}>
          <PlatformFacetAccordion classes={classes}
            activeTab={activeTab}
            isLoading={isLoading}
            deviceTypes={deviceTypes}
            showMoreFacets={showMoreFacets}
            platformIdAggregations={platformIdAggregations}
            setIsFileTypeChecked={setIsFileTypeChecked}
            setPlatformId={setPlatformId}
            toggleShowMore={toggleShowMore}
            handleFacetChange={handleFacetChange}
            clearFacetCategory={clearFacetCategory}
            hasSelectedJobsFacets={hasSelectedJobsFacets}
            getSelectedFacetsForTab={getSelectedFacetsForTab} />
        </Render.If>
        <ConditionalRender condition={!hideDateFacets && !isLoading && isDateAggregationsAvailable}>
          <div className={classes.accordionWrapper}>
            <Accordion className={classes.accordion}
              header={
                <div className={classes.categoryTitle}>
                  {translate('jobsearchengine:facets.category.createdOn.title')}
                  {hasSelectedJobsFacets(activeTab, 'createdOn') && (
                    <div onClick={function(e) {
                      e.stopPropagation();
                      e.preventDefault();
                      clearFacetCategory('createdOn', activeTab);
                      setDateRange?.(null);
                    }}
                    className={classes.clearAction}>
                      {translate('jobsearchengine:facets.clear')}
                    </div>
                  )}
                </div>
              }
              details={
                <div className={classes.details}>
                  {Object.entries(dateAggs).map(([key, group]: [string, any]) => (
                    <ConditionalRender key={key} condition={group.count > 0}>
                      <RadioButton key={key}
                        className={classes.radioButton}
                        disabled={isLoading}
                        label={
                          <div>
                            <span className={isLoading ? classes.checkBoxLabelDisabled : classes.checkBoxLabel}>
                              {translate(`jobsearchengine:facets.category.createdOn.${group.key}`)}
                            </span>
                            <span className={isLoading ? classes.checkBoxCountDisabled : classes.checkBoxCount}>
                              {` (${group.count})`}
                            </span>
                          </div>
                        }
                        checked={!!getSelectedFacetsForTab(activeTab).createdOn?.includes(group.key)}
                        onChange={function(e) { handleDateFacetChange(key, e.target.checked, activeTab); }} />
                    </ConditionalRender>
                  ))}
                </div>
              }
              backgroundColor={dlsColor('grey5')}
              borderBottom={'none'}
              padding={'0px 0px 15px'}
              isCondensedVariant
              defaultExpanded />
          </div>
        </ConditionalRender>
      </ConditionalRender>
    </Drawer>
  );
};

export default JobFacetsDrawer;
